import { useCallback, useMemo } from 'react';
import { useToast } from '../components/ToastManager/useToast';
import { useDispatch } from '../store/hooks';
import {
  setPreferencesActiveSection,
  setShowPreferences,
} from '../store/slices/preferences.slice';
import { AgentSourceId } from '../types/AgentSource';
import useAgentSourceIncludes from './useAgentSourceIncludes';
import { useDjSoftwareConnections } from './useDjSoftwareConnections';
import {
  StreamingServiceId,
  useStreamingConnections,
} from './useStreamingConnections';

export type AgentContextSource = {
  id: AgentSourceId;
  name: string;
  iconKey: string;
  connected: boolean;
  /** Attached sources are searched alongside the Crate Hackers library. */
  attached: boolean;
};

const DJ_SOFTWARE: {
  id: 'serato' | 'virtualdj';
  name: string;
  iconKey: string;
}[] = [
  { id: 'serato', name: 'Serato', iconKey: 'seratoDJPro' },
  { id: 'virtualdj', name: 'VirtualDJ', iconKey: 'virtualDJ' },
];

const STREAMING_ICONS: Record<StreamingServiceId, string> = {
  spotify: 'spotify',
  tidal: 'tidal',
  apple: 'apple',
};

/**
 * The optional sources a DJ can attach to the chat, with the actions to
 * attach, detach or connect one. Connected sources start attached.
 */
export const useAgentContextSources = () => {
  const dispatch = useDispatch();
  const { successToast, errorToast } = useToast();
  const djConnected = useDjSoftwareConnections();
  const { connections, connect } = useStreamingConnections();
  const { includes, setIncluded } = useAgentSourceIncludes();

  const sources = useMemo<AgentContextSource[]>(
    () => [
      ...DJ_SOFTWARE.map((item) => ({
        ...item,
        connected: djConnected[item.id],
        attached: includes[item.id] ?? true,
      })),
      ...connections.map((service) => ({
        id: service.id,
        name: service.name,
        iconKey: STREAMING_ICONS[service.id],
        connected: service.connected,
        attached: includes[service.id] ?? true,
      })),
    ],
    [connections, djConnected, includes],
  );

  const attach = useCallback(
    (id: AgentSourceId) => setIncluded(id, true),
    [setIncluded],
  );

  const detach = useCallback(
    (id: AgentSourceId) => setIncluded(id, false),
    [setIncluded],
  );

  const connectSource = useCallback(
    async (id: AgentSourceId) => {
      if (id === 'serato' || id === 'virtualdj') {
        dispatch(setPreferencesActiveSection('dj-software'));
        dispatch(setShowPreferences(true));
        return;
      }
      try {
        const message = await connect(id);
        setIncluded(id, true);
        successToast({ message });
      } catch (error) {
        errorToast({
          message: error instanceof Error ? error.message : 'Could not connect',
        });
      }
    },
    [connect, dispatch, errorToast, setIncluded, successToast],
  );

  return { sources, attach, detach, connectSource };
};

export default useAgentContextSources;
